import { useNavigate } from "@tanstack/react-router";
import type { AssignableRole } from "@unbox/api";
import { workspaceScopeRoles } from "@unbox/api";
import { ArrowLeft } from "@untitledui/icons";
import { useRef, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import {
  useAddMerchantsToWorkspace,
  useAddUsersToWorkspace,
  useCreateWorkspace,
  useUploadWorkspaceLogo,
} from "@/api/admin/workspace-mutations";
import { LogoPicker } from "@/components/application/logo-uploader/logo-picker";
import { IconTopNumber } from "@/components/base/application/progress-steps/step-base";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { FormField, FormItem } from "@/components/ui/form";
import { error, success } from "@/providers/ToastProvider";
import { WorkspaceBrandsPicker } from "./components/workspace-brands-picker";
import { WorkspaceUsersPicker } from "./components/workspace-users-picker";

const STEPS = [
  { id: "tiktok-shop", label: "TikTok Shop" },
  { id: "brands", label: "Merchants" },
  { id: "users", label: "Users" },
];

type FormValues = {
  name: string;
  customerType: "brand" | "agency";
  brandIds: string[];
  // Selected users → the role to grant them in this workspace (UNB-2001).
  userRoles: Record<string, AssignableRole>;
};

const CUSTOMER_TYPE_OPTIONS = [
  { id: "brand" as const, label: "Merchant" },
  { id: "agency" as const, label: "Agency" },
];

export const CreateWorkspacePage = () => {
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);

  const form = useForm<FormValues>({
    mode: "onTouched",
    defaultValues: {
      name: "",
      customerType: "brand",
      brandIds: [],
      userRoles: {},
    },
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutateAsync: createWorkspace } = useCreateWorkspace();
  const { mutateAsync: uploadLogo } = useUploadWorkspaceLogo();
  const { mutateAsync: addMerchants } = useAddMerchantsToWorkspace();
  const { mutateAsync: addUsers } = useAddUsersToWorkspace();

  const getStepFields = (step: number): (keyof FormValues)[] => {
    switch (step) {
      case 0:
        return ["name", "customerType"];
      case 1:
        return ["brandIds"];
      default:
        return [];
    }
  };

  const goToStep = (step: number) => {
    setCurrentStep(step);
    contentRef.current?.scrollTo({ top: 0 });
  };

  const handleNext = async () => {
    const isValid = await form.trigger(getStepFields(currentStep));
    if (isValid) goToStep(Math.min(currentStep + 1, STEPS.length - 1));
  };

  const handleStepClick = async (targetStep: number) => {
    if (targetStep <= currentStep) {
      goToStep(targetStep);
      return;
    }
    for (let i = currentStep; i < targetStep; i++) {
      const isValid = await form.trigger(getStepFields(i));
      if (!isValid) {
        goToStep(i);
        return;
      }
    }
    goToStep(targetStep);
  };

  const handleLogoUpload = async (file: File) => {
    if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    const url = URL.createObjectURL(file);
    setLogoFile(file);
    setLogoPreviewUrl(url);
  };

  const handleLogoDelete = async () => {
    if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    setLogoFile(null);
    setLogoPreviewUrl(null);
  };

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    setIsSubmitting(true);
    try {
      const workspace = await createWorkspace({ name: values.name.trim(), customerType: values.customerType });

      if (logoFile) {
        try {
          await uploadLogo({ workspaceId: workspace.id, file: logoFile });
        } catch {
          error("Workspace created, but logo upload failed");
        }
      }

      if (values.brandIds.length > 0) {
        await addMerchants({ workspaceId: workspace.id, merchantIds: values.brandIds });
      }

      if (Object.keys(values.userRoles).length > 0) {
        // Bucket users by chosen role (UNB-2001). Non-admins are granted per brand
        // (only when the workspace has brands); workspace admins need no brands —
        // authority spans the whole workspace via the users_to_workspaces row.
        const roleBuckets: Record<AssignableRole, string[]> = { user: [], merchant_admin: [], workspace_admin: [] };
        for (const [userId, role] of Object.entries(values.userRoles)) roleBuckets[role].push(userId);

        if (values.brandIds.length > 0) {
          for (const role of ["user", "merchant_admin"] as const) {
            if (roleBuckets[role].length > 0) {
              await addUsers({
                workspaceId: workspace.id,
                userIds: roleBuckets[role],
                merchants: Object.fromEntries(values.brandIds.map((b) => [b, role])),
              });
            }
          }
        }
        if (roleBuckets.workspace_admin.length > 0) {
          await addUsers({
            workspaceId: workspace.id,
            userIds: roleBuckets.workspace_admin,
            merchants: {},
            workspaceAdmin: true,
          });
        }
      }

      success("Workspace created");
      navigate({ to: "/admin/customers/$id", params: { id: workspace.id } });
    } catch (_err) {
      error("Failed to create workspace");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLastStep = currentStep === STEPS.length - 1;

  const brandIds = form.watch("brandIds");
  const userRoles = form.watch("userRoles");

  return (
    <div className="h-full flex flex-col px-12 overflow-auto">
      <h1 className="text-xl font-semibold text-primary mb-2">Create Workspace</h1>
      <button
        type="button"
        onClick={() => navigate({ to: "/admin/customers" })}
        className="flex items-center gap-1.5 text-sm font-medium text-tertiary hover:text-primary transition-colors mb-4 w-fit"
      >
        <ArrowLeft className="size-4" />
        Back to all Workspaces
      </button>

      <form onSubmit={(e) => e.preventDefault()} className="flex flex-col flex-1 min-h-0">
        <div
          style={{ gridTemplateColumns: `repeat(${STEPS.length}, minmax(0, 1fr))` }}
          className="grid w-full items-start justify-start gap-4"
        >
          {STEPS.map((step, index) => (
            <button key={step.id} type="button" onClick={() => handleStepClick(index)} className="cursor-pointer">
              <IconTopNumber
                title={step.label}
                description=""
                status={index < currentStep ? "complete" : index === currentStep ? "current" : "incomplete"}
                step={index + 1}
                size="sm"
                connector={index !== STEPS.length - 1}
              />
            </button>
          ))}
        </div>

        <div ref={contentRef} className="flex-1 min-h-0 pt-8 overflow-auto">
          <div className="flex flex-col gap-8 max-w-2xl mx-auto pb-8">
            {currentStep === 0 && (
              <div className="flex flex-col gap-6">
                <h2 className="text-md font-semibold text-primary">TikTok Shop</h2>

                <FormField
                  control={form.control}
                  name="name"
                  rules={{ required: "Workspace name is required" }}
                  render={({ field, fieldState }) => (
                    <FormItem className="flex flex-col">
                      <Input
                        isRequired
                        label="Company Workspace Name"
                        placeholder="Workspace name"
                        hint={
                          fieldState.error?.message ??
                          "This is how it will look in the navigation so make sure caps and spelling are as desired"
                        }
                        isInvalid={!!fieldState.error}
                        {...field}
                      />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="customerType"
                  rules={{ required: "Customer type is required" }}
                  render={({ field, fieldState }) => (
                    <FormItem className="flex flex-col">
                      <Select
                        isRequired
                        label="Customer type"
                        placeholder="Select customer type"
                        size="md"
                        hint={fieldState.error?.message ?? "Is this workspace for a merchant or an agency?"}
                        isInvalid={!!fieldState.error}
                        items={CUSTOMER_TYPE_OPTIONS}
                        selectedKey={field.value || null}
                        onSelectionChange={(v) => field.onChange(v)}
                        popoverClassName="dark-mode"
                      >
                        {(item) => (
                          <Select.Item key={item.id} id={item.id}>
                            {item.label}
                          </Select.Item>
                        )}
                      </Select>
                    </FormItem>
                  )}
                />

                <div className="flex flex-col gap-2">
                  <p className="text-sm font-medium text-secondary">Logo</p>
                  <LogoPicker currentLogoUrl={logoPreviewUrl} onUpload={handleLogoUpload} onDelete={handleLogoDelete} />
                </div>
              </div>
            )}

            {currentStep === 1 && (
              <div className="flex flex-col flex-1 overflow-hidden" style={{ minHeight: "400px" }}>
                <WorkspaceBrandsPicker
                  value={brandIds}
                  onChange={(ids) => form.setValue("brandIds", ids, { shouldValidate: true })}
                />
                {form.formState.errors.brandIds && (
                  <p className="text-sm text-error mt-2">{form.formState.errors.brandIds.message}</p>
                )}
              </div>
            )}

            {currentStep === 2 && (
              <div className="flex flex-col flex-1 overflow-hidden" style={{ minHeight: "400px" }}>
                <WorkspaceUsersPicker
                  value={userRoles}
                  onChange={(next) => form.setValue("userRoles", next)}
                  roleOptions={workspaceScopeRoles}
                />
              </div>
            )}
          </div>
        </div>

        <div className="shrink-0 flex items-center justify-between gap-3 py-6 border-t border-primary">
          <Button
            color="secondary"
            size="lg"
            type="button"
            onClick={() => {
              if (currentStep === 0) {
                navigate({ to: "/admin/customers" });
              } else {
                goToStep(currentStep - 1);
              }
            }}
          >
            {currentStep === 0 ? "Cancel" : "Back"}
          </Button>

          {isLastStep ? (
            <Button
              color="primary"
              size="lg"
              type="button"
              isDisabled={isSubmitting}
              onClick={() => form.handleSubmit(onSubmit)()}
            >
              {isSubmitting ? "Creating…" : "Create Workspace"}
            </Button>
          ) : (
            <Button color="primary" size="lg" type="button" onClick={handleNext}>
              Next
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};
